<template>
    <div class="tw-flex tw-h-full tw-flex-col tw-justify-between">
        <div>
            <div class="tw-mb-3 tw-flex tw-items-center tw-justify-between">
                <div class="tw-text-[32px] tw-font-bold">
                    {{ $t('integrations.link-your-whatsapp') }}
                </div>
                <AppToggle
                    v-model="manually"
                    :label="$t('integrations.link-manually')"
                    :disabled="loading || isDone || isFinished"
                />
            </div>

            <div class="tw-mb-3 tw-text-2xl tw-font-medium">
                <p v-if="!manually">
                    {{ $t('integrations.link-your-whatsapp-not-manually-description') }}
                </p>
                <p v-else>{{ $t('integrations.link-your-whatsapp-manually-description') }}</p>
            </div>

            <template v-if="!manually">
                <div class="tw-flex tw-flex-col tw-gap-4 tw-rounded-lg tw-bg-gray-100 tw-px-6 tw-py-4">
                    <div class="tw-text-lg tw-font-semibold tw-text-gray-900">
                        {{ $t('integrations.make-sure-you-fulfill-requirements') }}
                    </div>
                    <div class="tw-flex tw-items-center tw-gap-2 tw-text-gray-700">
                        <AppIcon name="user-01" />
                        <div class="tw-text-lg tw-font-medium">
                            {{ $t('integrations.you-have-access-to-verified-business-meta-account') }}
                        </div>
                    </div>

                    <div class="tw-flex tw-items-center tw-gap-2 tw-text-gray-700">
                        <AppIcon name="shield-tick" class="tw-text-gray-700"/>
                        <div class="tw-text-lg tw-font-medium">
                            {{ $t('integrations.business-use-case-compliant-with-whatsapp-and') }}
                            <a
                                href="https://business.whatsapp.com/policy#:~:text=4.%20Prohibited%20Organizations%20and%20Restrictions%20on%20Use"
                                target="_blank"
                                class="tw-text-lg tw-text-blue-600"
                            >{{ $t('integrations.meta-commerce-policy') }}</a
                            >
                        </div>
                    </div>

                    <div class="tw-flex tw-items-center tw-gap-2 tw-text-gray-700">
                        <AppIcon name="phone" />
                        <div class="tw-text-lg tw-font-medium">
                            {{ $t('integrations.the-phone-number-is-not-user-another-provider') }}
                        </div>
                    </div>
                    <div class="tw-flex tw-items-center tw-gap-2 tw-text-gray-700">
                        <AppIcon name="phone-01" />
                        <div class="tw-text-lg tw-font-medium">
                            {{ $t('integrations.you-can-verify-ownership-of-the-phone-number-by-sms') }}
                        </div>
                    </div>
                </div>
                <div class="tw-flex tw-flex-col">
                    <div class="tw-mt-5 tw-flex tw-gap-3">
                        <AppButton
                            :label="$t('integrations.link-your-meta-account')"
                            size="xl"
                            class="tw-rounded-full"
                            :disabled="loading"
                            @click="onLinkMetaAccount"
                            customClasses="tw-gap-3 !tw-bg-[#1877F2] !tw-text-white tw-rounded-xl"
                            startIcon="facebook"
                            endIcon="arrow-up-right"
                        />

                        <AppButton
                            v-if="isDone || isFinished"
                            :label="$t('integrations.next-step')"
                            size="xl"
                            class="tw-rounded-full"
                            type="button"
                            endIcon="assets/v2/image/icons/arrow-narrow-right.svg"
                            @click="nextStep"
                            :disabled="nextStepLoading || loading"
                        />
                    </div>
                    <div class="tw-text-sm tw-italic tw-text-gray-600 tw-mt-3" v-if="isDone || isFinished">
                        {{ $t('integrations.click-complete-after-complete-meta-flow') }}
                    </div>
                </div>
            </template>
            <template v-else>
                <AppForm @submit="onSubmitManually" class="tw-mt-2">
                    <div v-if="!isDone && !isFinished" class="tw-grid tw-grid-cols-2 tw-gap-x-3">
                        <div class="tw-col-span-2 lg:tw-col-span-1">
                            <AppInput
                                :label="$t('integrations.app-id')"
                                v-model="form.app_id"
                                :rules="['required']"
                                class="tw-w-full"
                            />
                        </div>
                        <div class="tw-col-span-2 lg:tw-col-span-1">
                            <AppInput
                                :label="$t('integrations.app-secret')"
                                v-model="form.app_secret"
                                :rules="['required']"
                                class="tw-w-full"
                            />
                        </div>
                        <div class="tw-col-span-2 lg:tw-col-span-1">
                            <AppInput
                                :label="$t('integrations.waba-id')"
                                v-model="form.waba_id"
                                :rules="['required']"
                                class="tw-w-full"
                            />
                        </div>
                        <div class="tw-col-span-2 lg:tw-col-span-1">
                            <AppInput
                                :label="$t('integrations.phone-number-id')"
                                v-model="form.phone_number_id"
                                :rules="['required']"
                                class="tw-w-full"
                            />
                        </div>
                        <div class="tw-col-span-2 lg:tw-col-span-1">
                            <AppInput
                                :label="$t('integrations.business-id')"
                                v-model="form.business_id"
                                :rules="['required']"
                                class="tw-w-full"
                            />
                        </div>
                        <div class="tw-col-span-2">
                            <AppTextArea
                                :label="$t('integrations.token')"
                                v-model="form.token"
                                :rules="['required']"
                                class="tw-w-full"
                            />
                        </div>
                    </div>
                    <div class="tw-mt-5 tw-flex tw-items-center tw-gap-3">
                        <AppButton
                            v-if="!isDone && !isFinished"
                            :label="$t('integrations.confirm-data')"
                            size="xl"
                            class="tw-rounded-full"
                            type="submit"
                            :disabled="submitLoading"
                        />
                        <AppButton
                            v-if="isDone || isFinished"
                            :label="$t('integrations.complete-setup')"
                            size="xl"
                            class="tw-rounded-full"
                            type="button"
                            endIcon="assets/v2/image/icons/arrow-narrow-right.svg"
                            @click="nextStep"
                            :disabled="nextStepLoading || isFinished"
                        />
                    </div>
                </AppForm>
            </template>
        </div>
        <div
            v-if="configs.brand == 'Linker'"
            class="tw-mt-7 tw-flex tw-flex-1 tw-items-end tw-justify-end"
        >
            <a :href="needHelpUrl" target="_blank">
                <AppButton
                    :label="$t('integrations.need-help')"
                    variant="secondary"
                    endIcon="assets/v2/image/icons/help-circle.svg"
                    class="tw-rounded-full"
                    size="xl"
                />
            </a>
        </div>
    </div>
</template>

<script setup>
import { configs } from '@/v2/system-configs';
import { initFacebookSdk, login } from '../../../../../../plugins/facebook-sdk';
import { Marketplace } from '../../../../entities/marketplace';
import { useLinkMarketplaceStore } from '../../../../stores/link-marketplace';
import { marketplaceStepStatusEnum } from '../../helper';
import { ref, computed } from 'vue';

const linkMarketplaceStore = useLinkMarketplaceStore();

const loading = ref(
	linkMarketplaceStore.currentStepData.status === marketplaceStepStatusEnum.InProgress,
);

const isDone = ref(
	linkMarketplaceStore.currentStepData.status === marketplaceStepStatusEnum.Complete,
);
const needHelpUrl =
    'https://www.linker.com.sa/help-center-articles-ar/integrating-a-whatsapp-number-with-linker';

const nextStepLoading = ref(false);
const submitLoading = ref(false);
const manually = ref(false);
const form = ref({
	app_id: '',
	app_secret: '',
	waba_id: '',
	phone_number_id: '',
	business_id: '',
	token: '',
});

const isFinished = computed(() => {
	return linkMarketplaceStore.currentStepData.status === marketplaceStepStatusEnum.Finished;
});

const onLinkMetaAccount = async () => {
	await initFacebookSdk();

	loading.value = true;
	const res = await login();
	if (res) {
		const success = await Marketplace.saveWhatsAppEmbeddedToken(
			linkMarketplaceStore.linkedMarketplaceId,
			{ authResponse: res },
		);
		if (!success) {
			document.location.href = '/store/subscribe/renew_package';
			return;
		}
        // Refetch the steps to update the store with the new status from backend
		await linkMarketplaceStore.refetchSteps();
		isDone.value = true;
	}
	loading.value = false;
};

const onSubmitManually = async () => {
	submitLoading.value = true;
	try {
		await Marketplace.manualSignUpCheck(form.value);
		const success = await Marketplace.saveWhatsAppManualToken(form.value);
		if (!success) {
			document.location.href = '/store/subscribe/renew_package';
			return;
		}
		isDone.value = true;
	} finally {
		submitLoading.value = false;
	}
};

const nextStep = async () => {
	nextStepLoading.value = true;
	try {
		await Marketplace.finishWizardStep(linkMarketplaceStore.currentStepData.id);
		await linkMarketplaceStore.refetchSteps();
	} finally {
		nextStepLoading.value = false;
	}
};
</script>

<style lang="scss" scoped></style>
